/**
 * Live tool / web-search activity rows for an assistant turn. Renders a
 * spinner while a tool call is in flight, a check when it has returned, and an
 * error marker if it failed. This is the agent's "showing its work" surface —
 * the backend only streams a coarse status, so this is derived from the
 * message's tool parts.
 */

import React from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

import type { ToolActivityItem } from '../engine/parts';

interface ToolActivityProps {
  items: ToolActivityItem[];
}

/**
 * Transient live-progress rows for the in-flight turn only. The caller renders
 * this exclusively while the turn is streaming, so it never lingers in the
 * conversation after completion — the draft card / response carries the
 * outcome.
 */
const ToolActivity: React.FC<ToolActivityProps> = ({ items }) => {
  if (items.length === 0) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 0.75 }}>
      {items.map((item) => (
        <Box
          key={item.key}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            color: 'text.secondary',
          }}
        >
          {item.status === 'running' && <CircularProgress size={13} thickness={6} />}
          {item.status === 'done' && (
            <CheckCircleOutlineIcon sx={{ fontSize: 15, color: 'success.main' }} />
          )}
          {item.status === 'error' && (
            <ErrorOutlineIcon sx={{ fontSize: 15, color: 'error.main' }} />
          )}
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {item.label}
            {item.status === 'running' ? '…' : ''}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export default React.memo(ToolActivity);
